import { apiFetch } from "@/lib/api";
import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { AlertTriangle, Clock, Play, Square, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const WorkHoldManager = ({ jobCard, onUpdate }) => {
  const [isHoldDialogOpen, setIsHoldDialogOpen] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [holdDescription, setHoldDescription] = useState('');
  const [heldBy, setHeldBy] = useState('');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Fetch employees when component mounts
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/employees`);
        setEmployees(response.data || []);
      } catch (error) {
        console.error('Error fetching employees:', error);
        setEmployees([]);
      }
    };

    fetchEmployees();
  }, [backendUrl]);

  const holdReasons = [
    { value: 'customer-approval', label: 'Customer Approval Needed' },
    { value: 'parts-unavailable', label: 'Parts Unavailable' },
    { value: 'technical-issue', label: 'Technical Issue' },
    { value: 'customer-payment', label: 'Customer Payment Pending' },
    { value: 'other', label: 'Other' }
  ];

  const putOnHold = async () => {
    if (!holdReason) {
      toast.error('Please select a hold reason');
      return;
    }

    if (!heldBy) {
      toast.error('Please select an employee');
      return;
    }

    setLoading(true);
    try {
      const response = await apiFetch(`${backendUrl}/api/jobcards/${jobCard.id}/hold`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reason: holdReason,
          description: holdDescription,
          held_by: heldBy
        }),
      });

      if (response.ok) {
        const updatedJobCard = await response.json();
        onUpdate(updatedJobCard);
        toast.success('Job card put on hold successfully');
        setIsHoldDialogOpen(false);
        setHoldReason('');
        setHoldDescription('');
        setHeldBy('');
      } else {
        const errorData = await response.json();
        toast.error(`Error: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Error putting job card on hold:', error);
      toast.error('Failed to put job card on hold');
    } finally {
      setLoading(false);
    }
  };

  const [resolvedBy, setResolvedBy] = useState('');
  const [isResolveDialogOpen, setIsResolveDialogOpen] = useState(false);

  const resolveHold = async () => {
    if (!resolvedBy) {
      toast.error('Please select who is resolving the hold');
      return;
    }

    setLoading(true);
    try {
      const response = await apiFetch(`${backendUrl}/api/jobcards/${jobCard.id}/resolve-hold`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          resolved_by: resolvedBy
        }),
      });

      if (response.ok) {
        const updatedJobCard = await response.json();
        onUpdate(updatedJobCard);
        toast.success('Hold resolved successfully');
        setIsResolveDialogOpen(false);
        setResolvedBy('');
      } else {
        const errorData = await response.json();
        toast.error(`Error: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Error resolving hold:', error);
      toast.error('Failed to resolve hold');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getHoldBadgeColor = (reason) => {
    const colors = {
      'customer-approval': 'bg-blue-100 text-blue-800',
      'parts-unavailable': 'bg-red-100 text-red-800',
      'technical-issue': 'bg-yellow-100 text-yellow-800',
      'customer-payment': 'bg-purple-100 text-purple-800',
      'other': 'bg-gray-100 text-gray-800'
    };
    return colors[reason] || colors.other;
  };

  const getReasonLabel = (reason) => {
    const labels = {
      'customer-approval': 'Customer Approval',
      'parts-unavailable': 'Parts Unavailable',
      'technical-issue': 'Technical Issue',
      'customer-payment': 'Payment Pending',
      'other': 'Other'
    };
    return labels[reason] || reason;
  };

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          Work Hold Management
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Current Status */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              {jobCard.is_on_hold ? (
                <Badge className="bg-red-100 text-red-800">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  ON HOLD
                </Badge>
              ) : (
                <Badge className="bg-green-100 text-green-800">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  ACTIVE
                </Badge>
              )}
              
              {jobCard.total_hold_duration_minutes > 0 && (
                <span className="text-sm text-gray-600">
                  Total hold time: {formatDuration(jobCard.total_hold_duration_minutes)}
                </span>
              )}
            </div>

            <div className="flex gap-2">
              {!jobCard.is_on_hold ? (
                <Dialog open={isHoldDialogOpen} onOpenChange={setIsHoldDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="put-on-hold-btn">
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Put on Hold
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Put Job Card on Hold</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Hold Reason *</Label>
                        <Select value={holdReason} onValueChange={setHoldReason}>
                          <SelectTrigger data-testid="hold-reason-select">
                            <SelectValue placeholder="Select hold reason" />
                          </SelectTrigger>
                          <SelectContent>
                            {holdReasons.map(reason => (
                              <SelectItem key={reason.value} value={reason.value}>
                                {reason.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={holdDescription}
                          onChange={(e) => setHoldDescription(e.target.value)}
                          placeholder="Describe the reason for hold..."
                          data-testid="hold-description-input"
                          rows={3}
                        />
                      </div>
                      
                      <div>
                        <Label>Held By *</Label>
                        <Select value={heldBy} onValueChange={setHeldBy}>
                          <SelectTrigger data-testid="hold-held-by-select">
                            <SelectValue placeholder="Select employee who is putting on hold" />
                          </SelectTrigger>
                          <SelectContent>
                            {employees
                              .filter(employee => 
                                employee.status === 'active' && 
                                (employee.department === 'service' || 
                                 employee.department === 'technical' || 
                                 employee.department === 'management' ||
                                 employee.role === 'technician' || 
                                 employee.role === 'supervisor' ||
                                 employee.role === 'manager' ||
                                 employee.position?.toLowerCase().includes('technician') ||
                                 employee.position?.toLowerCase().includes('mechanic') ||
                                 employee.position?.toLowerCase().includes('advisor') ||
                                 employee.position?.toLowerCase().includes('supervisor') ||
                                 employee.position?.toLowerCase().includes('manager'))
                              )
                              .map(employee => (
                                <SelectItem key={employee.id} value={employee.name}>
                                  {employee.name} - {employee.position || employee.role || employee.department}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setIsHoldDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button onClick={putOnHold} disabled={loading} data-testid="hold-confirm-btn">
                          {loading ? 'Putting on Hold...' : 'Put on Hold'}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              ) : (
                <Dialog open={isResolveDialogOpen} onOpenChange={setIsResolveDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-green-600 hover:text-green-700"
                      data-testid="resolve-hold-btn"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Resolve Hold
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Resolve Job Card Hold</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="p-3 bg-green-50 rounded">
                        <div className="text-sm text-green-800">
                          <strong>Current Hold:</strong>
                          <br />
                          Reason: {jobCard.current_hold?.reason}
                          <br />
                          Held since: {new Date(jobCard.current_hold?.held_at).toLocaleString()}
                        </div>
                      </div>
                      
                      <div>
                        <Label>Resolved By *</Label>
                        <Select value={resolvedBy} onValueChange={setResolvedBy}>
                          <SelectTrigger data-testid="resolve-hold-by-select">
                            <SelectValue placeholder="Select employee resolving the hold" />
                          </SelectTrigger>
                          <SelectContent>
                            {employees
                              .filter(employee => 
                                employee.status === 'active' && 
                                (employee.department === 'service' || 
                                 employee.department === 'technical' || 
                                 employee.department === 'management' ||
                                 employee.role === 'technician' || 
                                 employee.role === 'supervisor' ||
                                 employee.role === 'manager' ||
                                 employee.position?.toLowerCase().includes('technician') ||
                                 employee.position?.toLowerCase().includes('mechanic') ||
                                 employee.position?.toLowerCase().includes('advisor') ||
                                 employee.position?.toLowerCase().includes('supervisor') ||
                                 employee.position?.toLowerCase().includes('manager'))
                              )
                              .map(employee => (
                                <SelectItem key={employee.id} value={employee.name}>
                                  {employee.name} - {employee.position || employee.role || employee.department}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setIsResolveDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button onClick={resolveHold} disabled={loading} data-testid="resolve-hold-confirm-btn">
                          {loading ? 'Resolving...' : 'Resolve Hold'}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>

          {/* Current Hold Details */}
          {jobCard.is_on_hold && jobCard.current_hold && (
            <div className="p-4 border border-red-200 rounded-lg bg-red-50">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <Badge className={getHoldBadgeColor(jobCard.current_hold.reason)}>
                    {getReasonLabel(jobCard.current_hold.reason)}
                  </Badge>
                  {jobCard.current_hold.description && (
                    <p className="text-sm text-gray-700">{jobCard.current_hold.description}</p>
                  )}
                  <div className="text-xs text-gray-600">
                    Held by: {jobCard.current_hold.held_by || 'Unknown'} • 
                    Held at: {new Date(jobCard.current_hold.held_at).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Hold History */}
          {jobCard.hold_history && jobCard.hold_history.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Hold History</h4>
              <div className="max-h-40 overflow-y-auto space-y-2">
                {jobCard.hold_history.map((hold, index) => (
                  <div key={index} className="p-2 border rounded text-sm">
                    <div className="flex items-center justify-between">
                      <Badge className={`${getHoldBadgeColor(hold.reason)} text-xs`}>
                        {getReasonLabel(hold.reason)}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        {hold.is_resolved ? 'Resolved' : 'Active'}
                      </span>
                    </div>
                    {hold.description && (
                      <p className="text-gray-600 mt-1">{hold.description}</p>
                    )}
                    <div className="text-xs text-gray-500 mt-1">
                      {new Date(hold.held_at).toLocaleString()}
                      {hold.resolved_at && (
                        <> - {new Date(hold.resolved_at).toLocaleString()}</>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default WorkHoldManager;